import { ReactNode, useRef, useEffect, useState, useCallback } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { useModalEscClose } from '../../../utils/hooks/use-modal-esc';
import { useModalFocusTrap } from '../../../utils/hooks/use-modal-focus-trap';
import { CloseIcon } from '../../shared/icons';
import Button from '../ui-kit/button';
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;

  title?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  caption?: any;
  children?: ReactNode;

  size?: 'small' | 'medium' | 'large' | 'x-large';

  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  destructive?: boolean;
  autoFocusConfirm?: boolean;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  caption,
  children,
  size = 'small',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmDisabled = false,
  destructive = true,
  autoFocusConfirm = false
}: ConfirmationModalProps) {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animationClass, setAnimationClass] = useState('');
  useModalFocusTrap(isOpen, contentRef);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => setAnimationClass('modal-fade-in'), 10);
    } else {
      setAnimationClass('modal-fade-out');
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  useModalEscClose(isOpen, handleClose);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return 'max-w-sm min-w-[20%] max-h-[70vh]';
      case 'medium':
        return 'max-w-md min-w-[30%] max-h-[80vh]';
      case 'large':
        return 'max-w-lg min-w-[70%] md:mx-20 max-h-[85vh]';
      case 'x-large':
        return 'max-w-5xl min-w-[90%] md:mx-20 max-h-[90vh]';
      default:
        return '';
    }
  };

  const overlayStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '0 1rem',
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  };

  const contentStyle = {
    position: 'relative' as const,
    border: `1px solid ${getColour('hairline')}`,
    background: getColour('secondLayer'),
    width: '100%',
    borderRadius: '1rem',
    color: getColour('normalText'),
    outline: 'none',
    display: 'flex',
    flexDirection: 'column' as const
  };

  if (!shouldRender) return null;

  const hasChildren =
    !!children && !(typeof children === 'string' && children.trim() === '');

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault(); // stop navigation/refresh
    await onConfirm();
  };

  return (
    <>
      <div
        style={overlayStyle}
        className={cn('modal-overlay', animationClass)}
        onMouseDown={e => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div
          ref={contentRef}
          style={contentStyle}
          className={cn(
            'outline-none flex flex-col shadow-pop',
            getSizeClass()
          )}
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'confirm-modal-title' : undefined}
        >
          <div
            className="flex-shrink-0 w-full flex items-start justify-between gap-x-2 py-4 px-6 border-b rounded-t-2xl"
            style={{ borderColor: getColour('hairline') }}
          >
            <div className="w-fit flex flex-col">
              {title && (
                <h2 id="confirm-modal-title" className="font-semibold text-lg">
                  {title}
                </h2>
              )}
              {caption && <p className="text-wrap text-start">{caption}</p>}
            </div>
            <button
              type="button"
              className="border-none bg-transparent p-0 m-0 flex items-center justify-center hover:text-primary self-start"
              style={{ color: getColour('icon') }}
              onClick={handleClose}
              aria-label={t('close')}
            >
              <CloseIcon />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 min-h-0 w-full flex flex-col"
          >
            <button type="submit" className="hidden" aria-hidden="true" />

            {hasChildren ? (
              <div className="overflow-y-auto py-4 px-5 flex-1 flex flex-col">
                {children}
              </div>
            ) : (
              !autoFocusConfirm && (
                <input
                  autoFocus
                  className="sr-only"
                  aria-hidden="true"
                  type="text"
                  style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: 'none'
                  }}
                />
              )
            )}

            <div
              className="flex-shrink-0 w-full flex justify-end items-center gap-x-2 px-6 py-3 border-t rounded-b-2xl"
              style={{
                borderColor: getColour('hairline'),
                backgroundColor: getColour('secondLayer')
              }}
            >
              <Button
                type="button"
                onClick={handleClose}
                label={cancelLabel}
                size="small"
                variant="smoke"
              />
              <Button
                type="submit"
                disabled={confirmDisabled}
                label={confirmLabel}
                size="small"
                variant={destructive ? 'danger' : 'primary'}
                {...(autoFocusConfirm ? { autoFocus: true } : {})}
              />
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .modal-overlay { transition: opacity 200ms ease-in-out; }
        .modal-fade-in { opacity: 1; }
        .modal-fade-out { opacity: 0; }
      `}</style>
    </>
  );
}
